import { css } from '@emotion/react';
import { Box, Table as KlikTable } from '@lego/klik-ui';
import type { RowData } from '@tanstack/react-table';
import { useCallback, useEffect, useRef, useState } from 'react';
import { TableHeader } from '../headers/TableHeader';
import { Loader } from '../Loader';
import type { Row } from './appTable';
import { TableBodyRow } from './TableBodyRow';
import { TableHead } from './TableHead';
import { TablePagination } from './TablePagination';
import { TableToolbar } from './TableToolbar';
import type { Props } from './types';
import { useTableInstance } from './useTableInstance';

const validatingStyle = css`
  @keyframes table-loading-pulse {
    from {
      opacity: 0.3;
    }
    to {
      opacity: 0.7;
    }
  }
  & > tr > td > * {
    animation: table-loading-pulse 1s infinite alternate ease-in-out;
  }
`;

export const Table = <T extends RowData>(props: Props<T>) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showShadow, setShowShadow] = useState(false);

  const updateShadow = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const hasOverflow = el.scrollWidth > el.clientWidth;
    const isScrolledFromEnd = el.scrollWidth - el.scrollLeft - el.clientWidth > 1;
    setShowShadow(hasOverflow && isScrolledFromEnd);
  }, []);

  useEffect(() => {
    if (props.isLoading) return;
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateShadow, { passive: true });
    const observer =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateShadow) : undefined;
    observer?.observe(el);
    updateShadow();
    return () => {
      el.removeEventListener('scroll', updateShadow);
      observer?.disconnect();
    };
  }, [updateShadow, props.isLoading]);

  const {
    table,
    columnOrder,
    columnVisibility,
    pagination,
    resetTable,
    setColumnOrder,
    setColumnVisibility,
    setPagination,
  } = useTableInstance({
    columns: props.columns,
    data: props.data,
    enableRowSelection: props.enableRowSelection,
    getRowCanSelect: props.getRowCanSelect,
    getRowId: props.getRowId,
    hasExpandableRows: !!props.expandableRow,
    hideControls: props.hideControls,
    serverSide: props.serverSide,
  });

  const itemCount = props.serverSide?.rowCount ?? props.count;
  const emptyMessage =
    props.filteredEmptyMessage &&
    props.data.length > 0 &&
    table.getFilteredRowModel().rows.length === 0
      ? props.filteredEmptyMessage
      : props.emptyMessage;
  const contentPaddingX = props.nestedStyling ? 0 : [4, 6];
  const outerBoxStyles = props.nestedStyling
    ? {}
    : {
        boxShadow: 'md',
        paddingBottom: [3, 5],
        paddingTop: 0,
        paddingX: 0,
      };

  return (
    <Box
      background="white"
      minHeight="16rem"
      position="relative"
      rounded="md"
      whiteSpace="nowrap"
      width="100%"
      {...outerBoxStyles}
    >
      {props.isLoading ? (
        <Box backgroundColor="white" paddingX={contentPaddingX} paddingY={[3, 5]}>
          {props.title && <TableHeader title={props.title} />}
          <Loader />
        </Box>
      ) : (
        <>
          <TableToolbar
            addButton={props.addButton}
            columnOrder={columnOrder}
            columns={table.getAllLeafColumns()}
            columnVisibility={columnVisibility}
            contentPaddingX={contentPaddingX}
            count={itemCount}
            description={props.description}
            hideControls={props.hideControls}
            isValidating={props.isValidating}
            refresh={props.refresh}
            resetTable={resetTable}
            setColumnOrder={setColumnOrder}
            setColumnVisibility={setColumnVisibility}
            title={props.title}
            toolbarAddon={props.toolbarAddon}
          />
          <Box overflowX="auto" overflowY="hidden" ref={scrollRef} width="100%">
            <KlikTable
              marginX={contentPaddingX}
              minWidth={['calc(100% - 2rem)', 'calc(100% - 3rem)']}
              style={{ width: props.hideControls ? '100%' : 'auto' }}
              variant="simple"
            >
              <TableHead
                enableRowSelection={props.enableRowSelection}
                hasExpandableRow={!!props.expandableRow}
                showStickyShadow={showShadow}
                table={table}
              />
              <KlikTable.Body css={props.isValidating && validatingStyle}>
                {table.getRowModel().rows.map((row: Row<T>, index: number) => (
                  <TableBodyRow
                    enableRowSelection={props.enableRowSelection}
                    expandableRow={props.expandableRow}
                    getRowProps={props.getRowProps}
                    index={index}
                    key={row.id}
                    onRowClick={props.onRowClick}
                    row={row}
                    rowLink={props.rowLink}
                    showStickyShadow={showShadow}
                  />
                ))}
              </KlikTable.Body>
            </KlikTable>
          </Box>
          {table.getRowModel().rows.length === 0 && emptyMessage && (
            <Box
              alignItems="center"
              display="flex"
              justifyContent="center"
              minHeight="8rem"
              paddingX={contentPaddingX}
              width="100%"
            >
              {emptyMessage}
            </Box>
          )}
          {!props.hideControls && (
            <TablePagination
              contentPaddingX={contentPaddingX}
              pageCount={table.getPageCount()}
              pageIndex={table.state.pagination.pageIndex}
              pageSize={pagination.pageSize}
              setPageIndex={table.setPageIndex}
              setPagination={setPagination}
            />
          )}
        </>
      )}
    </Box>
  );
};
